// @djsem/shared — Template, Preset, Starter Content Library (Prompt 34).

import { type DJSEMValidationResult, toDJSEMValidationResult, type ValidationResult } from "./index";
import { SeqCounter } from "./runtime";

// ---- contracts --------------------------------------------------------------

/** The kind of resource a template/preset instantiates. */
export type DJSEMTemplateType =
  | "brand_world" | "visual_scene" | "lighting_scene" | "typography_style"
  | "set_structure" | "transition_plan" | "release_campaign" | "show_pack"
  | "venue_profile" | "adapter_mapping" | "export_preset";

/** Plan tier gating access to a template, ordered lowest to highest. */
export type DJSEMPlanLevelTemplate = "free_starter" | "creator" | "creator_pro" | "venue" | "enterprise";

/** A stored template, preset, or starter-content item in the template library. */
export interface DJSEMTemplate {
  id: string;
  templateType: DJSEMTemplateType;
  label: string;
  description: string;
  tags: string[];
  genreTags: string[];
  moodTags: string[];
  useCases: string[];
  requiredPlan: DJSEMPlanLevelTemplate;
  payload: unknown;
  previewAssetId?: string;
  authorType: "system" | "user" | "marketplace_creator";
  createdBy?: string;
  status: "draft" | "published_private" | "published_system" | "archived";
  version: number;
  sourceTemplateId?: string; // provenance
  sourceTemplateVersion?: number;
  warnings: string[];
  createdAt: string;
  updatedAt: string;
}

/** A curated bundle of templates for a specific use case (e.g. a starter pack). */
export interface DJSEMTemplatePack {
  id: string;
  label: string;
  description: string;
  templateIds: string[];
  useCase: string;
  requiredPlan: DJSEMPlanLevelTemplate;
  warnings: string[];
}

/** Result of instantiating a template into a new project resource. */
export interface DJSEMTemplateInstantiationResult {
  templateId: string;
  templateVersion: number;
  instantiatedResourceId: string;
  warnings: string[];
}

// ---- inputs -----------------------------------------------------------------

/** Input for {@link DJSEMTemplateLibraryService.createTemplate}. */
export interface CreateTemplateInput extends Omit<DJSEMTemplate, "id" | "createdAt" | "updatedAt" | "version" | "warnings"> {
  id?: string; createdAt?: string;
}
/** Input for {@link DJSEMTemplateLibraryService.searchTemplates}. */
export interface SearchTemplatesInput { templateType?: DJSEMTemplateType; useCase?: string; genreTag?: string; moodTag?: string; text?: string; }
/** Input for {@link DJSEMTemplateLibraryService.instantiateTemplate}. */
export interface InstantiateTemplateInput { template: DJSEMTemplate; targetProjectId: string; userPlanLevel: DJSEMPlanLevelTemplate; }

// ---- helpers ----------------------------------------------------------------

const AT = "1970-01-01T00:00:00.000Z";
const _seq = new SeqCounter();

const PLAN_ORDER: DJSEMPlanLevelTemplate[] = ["free_starter", "creator", "creator_pro", "venue", "enterprise"];

function planMeetsRequirement(user: DJSEMPlanLevelTemplate, required: DJSEMPlanLevelTemplate): boolean {
  return PLAN_ORDER.indexOf(user) >= PLAN_ORDER.indexOf(required);
}

// ---- service ----------------------------------------------------------------

/** Stores, searches, and instantiates templates/presets/starter content. Bounded so a long-lived instance cannot grow without limit. */
export class DJSEMTemplateLibraryService {
  private templates: DJSEMTemplate[] = [];
  // Bounded so a long-lived library instance cannot grow without limit.
  constructor(private maxTemplates = 10000) {}

  /**
   * Create a new template at version 1.
   * @param input - The template fields (id/version/timestamps/warnings are generated).
   * @returns The created template.
   */
  createTemplate(input: CreateTemplateInput): DJSEMTemplate {
    const t: DJSEMTemplate = { ...input, id: input.id ?? `tmpl_${_seq.next()}`, version: 1, warnings: [], createdAt: input.createdAt ?? AT, updatedAt: input.createdAt ?? AT };
    this.templates.push(t);
    if (this.templates.length > this.maxTemplates) this.templates.splice(0, this.templates.length - this.maxTemplates);
    return t;
  }

  /**
   * Validate a template's required fields.
   * @param t - The template to validate.
   * @returns The validation result.
   */
  validateTemplate(t: DJSEMTemplate): DJSEMValidationResult {
    const issues: ValidationResult["issues"] = [];
    if (!t.label.trim()) issues.push({ path: "$.label", message: "required" });
    if (!t.templateType) issues.push({ path: "$.templateType", message: "required" });
    if (t.payload === undefined || t.payload === null) issues.push({ path: "$.payload", message: "required" });
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "template");
  }

  /** @returns A copy of all stored templates. */
  listTemplates(): DJSEMTemplate[] { return [...this.templates]; }

  /**
   * Search templates by type, use case, tags, or free text.
   * @param input - The filters to apply (all optional; combined with AND).
   * @returns The matching templates.
   */
  searchTemplates(input: SearchTemplatesInput): DJSEMTemplate[] {
    return this.templates.filter(t => {
      if (input.templateType && t.templateType !== input.templateType) return false;
      if (input.useCase && !t.useCases.includes(input.useCase)) return false;
      if (input.genreTag && !t.genreTags.includes(input.genreTag)) return false;
      if (input.moodTag && !t.moodTags.includes(input.moodTag)) return false;
      if (input.text && !t.label.toLowerCase().includes(input.text.toLowerCase()) && !t.description.toLowerCase().includes(input.text.toLowerCase())) return false;
      return true;
    });
  }

  /**
   * Instantiate a template into a new project resource, gated by plan level. Does not
   * mutate the original template.
   * @param input - The template, target project, and the user's plan level.
   * @returns The new resource id, or an empty id with a warning if the plan is insufficient.
   */
  instantiateTemplate(input: InstantiateTemplateInput): DJSEMTemplateInstantiationResult {
    const warnings: string[] = [];
    if (!planMeetsRequirement(input.userPlanLevel, input.template.requiredPlan)) {
      return { templateId: input.template.id, templateVersion: input.template.version, instantiatedResourceId: "", warnings: [`Plan ${input.userPlanLevel} does not meet required ${input.template.requiredPlan}`] };
    }
    // Instantiation produces a new resource (ID) — does NOT mutate original template.
    const resourceId = `inst_${_seq.next()}_${input.template.id}`;
    return { templateId: input.template.id, templateVersion: input.template.version, instantiatedResourceId: resourceId, warnings };
  }
}

// ---- starter packs ----------------------------------------------------------

/**
 * Build the canonical set of starter template packs shipped by default.
 * @returns The starter packs (wedding, bar warmup, club, genre identities, social/visual presets).
 */
export function createStarterPacks(): DJSEMTemplatePack[] {
  return [
    { id: "sp_wedding", label: "Wedding Reception", description: "Warm, elegant starter", templateIds: [], useCase: "wedding", requiredPlan: "free_starter", warnings: [] },
    { id: "sp_bar", label: "Bar Warmup", description: "Relaxed lounge vibes", templateIds: [], useCase: "bar_warmup", requiredPlan: "free_starter", warnings: [] },
    { id: "sp_club", label: "Small Club Peak Hour", description: "High energy club set", templateIds: [], useCase: "small_club", requiredPlan: "free_starter", warnings: [] },
    { id: "sp_tech", label: "Tech House Release", description: "Dark minimal tech house", templateIds: [], useCase: "tech_house_identity", requiredPlan: "creator", warnings: [] },
    { id: "sp_afro", label: "Afro House Release", description: "Warm melodic afro", templateIds: [], useCase: "afro_house_identity", requiredPlan: "creator", warnings: [] },
    { id: "sp_dnb", label: "Drum and Bass Visualizer", description: "High-energy DnB visuals", templateIds: [], useCase: "drum_and_bass_identity", requiredPlan: "creator", warnings: [] },
    { id: "sp_yt", label: "YouTube Visualizer", description: "Horizontal video visualizer", templateIds: [], useCase: "youtube_visualizer", requiredPlan: "creator", warnings: [] },
    { id: "sp_canvas", label: "Spotify Canvas", description: "Short loop for Spotify Canvas", templateIds: [], useCase: "spotify_canvas", requiredPlan: "creator", warnings: [] },
    { id: "sp_ig", label: "Instagram Teaser", description: "Vertical social clip", templateIds: [], useCase: "instagram_teaser", requiredPlan: "free_starter", warnings: [] },
    { id: "sp_proj", label: "Projection Loop Pack", description: "1920x1080 projection loops", templateIds: [], useCase: "projection_loop", requiredPlan: "creator_pro", warnings: [] },
    { id: "sp_led", label: "LED Wall Loop Pack", description: "Custom LED loops", templateIds: [], useCase: "led_wall_loop", requiredPlan: "venue", warnings: [] },
    { id: "sp_fest", label: "Festival Intro (Future)", description: "Placeholder festival intro", templateIds: [], useCase: "festival_intro", requiredPlan: "creator_pro", warnings: ["Future placeholder"] },
  ];
}
